import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCheck, FiUser, FiSave, FiTrash2, FiCalendar, FiInfo, FiUsers, FiGrid, FiSearch, FiCheckSquare, FiSquare, FiChevronDown, FiMail, FiDownload } from 'react-icons/fi';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';
import apiService from '../../services/api';
import QRCodeGenerator from '../common/QRCodeGenerator';
import Notification from '../common/Notification';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 1rem;
  }
`;

const ModalContent = styled(motion.div)`
  background-color: white;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid #E5E3DF;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    max-width: 100%;
    max-height: 95vh;
    border-radius: 16px 16px 0 0;
    margin-top: auto;
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.2);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    font-weight: 600;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: var(--text-primary);
    background-color: #f9f9f9;
  }
`;

const SessionInfo = styled.div`
  background-color: #f9f9f9;
  border: 1px solid #E5E3DF;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const SessionTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.75rem 0;
`;

const SessionDetails = styled.div`
  display: flex;
  gap: 2rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const SessionDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SessionNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  color: #f59e0b;
  margin-top: 0.75rem;
  padding: 0.625rem 0.75rem;
  background-color: rgba(245, 158, 11, 0.1);
  border-radius: 8px;
`;

const StudentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const StudentItem = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 0.875rem 1rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  &:hover {
    border-color: rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const StudentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.75rem;
  }
`;

const StudentRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    flex-basis: 100%;
  }
`;

const StudentRowMiddle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    flex-basis: 100%;
  }
`;

const StudentRowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
    flex-basis: 100%;
  }
`;

const StudentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
`;

const StudentAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 0.8125rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: 0.75rem;
  }
`;

const StudentName = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 0.875rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const StudentEmail = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  color: #6B7280;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  
  &.present {
    background-color: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }
  
  &.absent {
    background-color: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
    padding: 3px 8px;
  }
`;

const StatusTimestamp = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.6875rem;
  color: #6B7280;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.625rem;
  }
`;

const StatusActionButton = styled.button`
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: white;
  color: var(--text-primary);
  
  &:hover {
    background: #f9f9f9;
    border-color: rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
    padding: 0.4375rem 0.875rem;
  }
`;

const NotesToggleButton = styled.button`
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: none;
  color: #6B7280;
  
  &:hover {
    color: var(--text-primary);
    background: #f9f9f9;
  }
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
    padding: 0.4375rem 0.625rem;
  }
`;

const NotesContainer = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`;

const NotesInput = styled.textarea`
  padding: 0.625rem 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: white;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.8125rem;
  width: 100%;
  min-height: 60px;
  resize: vertical;
  transition: all 0.2s ease;
  line-height: 1.5;
  
  &:hover {
    border-color: rgba(0, 0, 0, 0.12);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &::placeholder {
    color: #9CA3AF;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    min-height: 56px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1.5rem;
    padding-top: 0.75rem;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  
  &.primary {
    background-color: var(--primary-color);
    color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    
    &:hover:not(:disabled) {
      background-color: var(--primary-hover, #c9a066);
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
  
  &.secondary {
    background-color: white;
    color: var(--text-primary);
    border: 1px solid #E5E3DF;
    
    &:hover {
      background-color: #f9f9f9;
      border-color: var(--primary-color);
      transform: translateY(-1px);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }
  
  &.danger {
    background-color: #ef4444;
    color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    
    &:hover:not(:disabled) {
      background-color: #dc2626;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    font-size: 0.875rem;
    min-height: 48px;
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #ef4444;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  color: #10b981;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const EmptyDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const EmptyAction = styled.div`
  display: flex;
  justify-content: center;
`;

const ExportButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: 1px solid var(--primary-color);
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(216, 184, 137, 0.1);
  color: var(--primary-color);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  &:hover:not(:disabled) {
    background: rgba(216, 184, 137, 0.15);
    border-color: var(--primary-hover, #c9a066);
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 0.875rem 1rem;
    font-size: 0.875rem;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  display: flex;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
`;

const SearchInputContainer = styled.div`
  position: relative;
  flex: 1;
`;

const StatusFilter = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #E5E3DF;
  border-radius: 10px;
  background: white;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  min-width: 140px;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 12px;
  padding-right: 2.5rem;
  
  &:hover {
    border-color: var(--primary-color);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border: 1px solid #E5E3DF;
  border-radius: 10px;
  background: white;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  box-sizing: border-box;
  height: 44px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  pointer-events: none;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchResults = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: 1.25rem;
  padding: 0.875rem 1rem;
  background: #f9f9f9;
  border-radius: 10px;
  border: 1px solid #E5E3DF;
  text-align: center;
  font-weight: 500;
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  
  &:hover {
    color: var(--text-primary);
    background: #f9f9f9;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DashboardStatsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #E5E3DF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
`;

const StatsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const StatsTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const StatsToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #E5E3DF;
  border-radius: 8px;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9f9f9;
    border-color: var(--primary-color);
  }
  
  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
  }
  
  &.expanded svg {
    transform: rotate(180deg);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 1.5rem 1rem;
  background: #f9f9f9;
  border-radius: 12px;
  border: 1px solid #E5E3DF;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0.75rem;
  }
`;

const StatLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
`;

const StatValue = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  
  &.present {
    color: #10b981;
  }
  
  &.absent {
    color: #ef4444;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;


const BulkActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: #f9f9f9;
  border-radius: 12px;
  border: 1px solid #E5E3DF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
    padding: 1rem;
  }
`;

const SelectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    width: 100%;
  }
`;

const SelectAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #E5E3DF;
  border-radius: 8px;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--background-tertiary);
    border-color: var(--primary-color);
  }
`;

const BulkActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const BulkActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--background-primary);
  
  &.present {
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
    border-color: var(--success-color);
    
    &:hover:not(:disabled) {
      background: rgba(104, 211, 145, 0.2);
      border-color: var(--success-color);
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
  
  &.absent {
    background: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
    border-color: var(--error-color);
    
    &:hover:not(:disabled) {
      background: rgba(252, 129, 129, 0.2);
      border-color: var(--error-color);
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 0.875rem 1rem;
    font-size: 0.9rem;
  }
`;

const DetailedAttendanceSummary = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: var(--bg-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);
`;

const SummarySection = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SummaryTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  &.present {
    color: var(--success-color);
  }
  
  &.absent {
    color: var(--error-color);
  }
`;

const SummaryStudentList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const SummaryStudentName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  color: var(--text-primary);
  
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
  
  &.present {
    border-color: var(--success-color);
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
  }
  
  &.absent {
    border-color: var(--error-color);
    background: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
  }
`;

const SummaryStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: var(--bg-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
`;


const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const Checkbox = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--primary-color);
    background: var(--background-tertiary);
  }
  
  &.checked {
    color: var(--primary-color);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: var(--text-muted);
    
    &:hover {
      color: var(--text-muted);
      background: none;
    }
  }
`;

function TakeAttendanceModal({ isOpen, onClose, sessionId, onAttendanceUpdated, onAttendanceChanged }) {
  
  const [session, setSession] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showQRCode, setShowQRCode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [showSummary, setShowSummary] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [notification, setNotification] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const [expandedNotes, setExpandedNotes] = useState(new Set());

  const fetchSessionData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await apiService.getAttendanceSession(sessionId);
      
      if (response.success) {
        setSession(response.session);
        setAttendanceRecords(response.session.records);
      } else {
        setError(response.message || 'Failed to fetch attendance session');
      }
    } catch (error) {
      console.error('Error fetching attendance session:', error);
      setError('Failed to fetch attendance session. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isOpen && sessionId) {
      fetchSessionData();
      setHasUnsavedChanges(false);
      setSearchTerm('');
    } else {
      // Clear selections and expanded notes when modal closes
      setSelectedStudents(new Set());
      setExpandedNotes(new Set());
    }
  }, [isOpen, sessionId, fetchSessionData]);

  const handleStatusChange = (studentId, status) => {
    setAttendanceRecords(prev => {
      const updated = prev.map(record => 
        record.student_id === studentId 
          ? { ...record, status, updated_at: new Date().toISOString() } 
          : record
      );
      
      // Calculate updated statistics
      const presentCount = updated.filter(r => r.status === 'present').length;
      const absentCount = updated.filter(r => r.status === 'absent').length;
      const totalStudents = updated.length;
      const attendanceRate = totalStudents > 0 ? Math.round((presentCount / totalStudents) * 100) : 0;
      
      // Notify parent component of the change
      if (onAttendanceChanged) {
        onAttendanceChanged({
          present_count: presentCount,
          absent_count: absentCount,
          total_students: totalStudents,
          attendance_rate: attendanceRate
        });
      }
      
      return updated;
    });
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
    
    // Clear any existing success/error messages when making changes
    setSuccess('');
    setError('');
  };

  const handleNotesChange = (studentId, notes) => {
    setAttendanceRecords(prev => 
      prev.map(record => 
        record.student_id === studentId 
          ? { ...record, notes } 
          : record
      )
    );
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
  };

  const toggleNotesExpansion = (studentId) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const handleStudentSelection = (studentId) => {
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      
      
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allStudentIds = filteredRecords.map(record => record.student_id);
    const allSelected = allStudentIds.every(id => selectedStudents.has(id));
    
    
    if (allSelected) {
      // Deselect all
      setSelectedStudents(new Set());
    } else {
      // Select all
      setSelectedStudents(new Set(allStudentIds));
    }
  };

  const handleBulkStatusChange = async (status) => {
    if (selectedStudents.size === 0) return;
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Update local state first
      setAttendanceRecords(prev => {
        const updated = prev.map(record => 
          selectedStudents.has(record.student_id)
            ? { ...record, status, updated_at: new Date().toISOString() }
            : record
        );
        
        // Calculate updated statistics
        const presentCount = updated.filter(r => r.status === 'present').length;
        const absentCount = updated.filter(r => r.status === 'absent').length;
        const totalStudents = updated.length;
        const attendanceRate = totalStudents > 0 ? Math.round((presentCount / totalStudents) * 100) : 0;
        
        // Notify parent component of the change
        if (onAttendanceChanged) {
          onAttendanceChanged({
            present_count: presentCount,
            absent_count: absentCount,
            total_students: totalStudents,
            attendance_rate: attendanceRate
          });
        }
        
        return updated;
      });
      
      // Prepare records for API call
      const records = attendanceRecords.map(record => ({
        studentId: record.student_id,
        status: selectedStudents.has(record.student_id) ? status : record.status,
        notes: record.notes || null
      }));

      // Save to database
      const response = await apiService.updateAttendanceRecords(sessionId, records);
      
      if (response.success) {
        setSuccess(`${selectedStudents.size} students marked as ${status} successfully`);
        setHasUnsavedChanges(false);
        if (onAttendanceUpdated) {
          onAttendanceUpdated();
        }
      } else {
        setError(response.message || `Failed to mark students as ${status}`);
        // Revert local changes on error
        setAttendanceRecords(attendanceRecords);
      }
    } catch (error) {
      console.error(`Error marking students as ${status}:`, error);
      setError(`Failed to mark students as ${status}. Please try again.`);
      // Revert local changes on error
      setAttendanceRecords(attendanceRecords);
    } finally {
      setSaving(false);
      setSelectedStudents(new Set()); // Clear selection after bulk action
    }
  };

  const downloadAttendanceCSV = () => {
    if (!session || attendanceRecords.length === 0) return;
    
    const headers = ['Name', 'Email', 'Status', 'Check-in Time', 'Notes'];
    const rows = attendanceRecords.map(record => [
      `${record.first_name} ${record.last_name}`,
      record.email,
      record.status === 'present' ? 'Present' : 'Absent',
      record.updated_at ? new Date(record.updated_at).toLocaleString() : '',
      record.notes || ''
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${session.title}_attendance_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const emailAttendanceCSV = async () => {
    if (!session || attendanceRecords.length === 0) return;
    
    try {
      setSendingEmail(true);
      
      const headers = ['Name', 'Email', 'Status', 'Check-in Time', 'Notes'];
      const rows = attendanceRecords.map(record => [
        `${record.first_name} ${record.last_name}`,
        record.email,
        record.status === 'present' ? 'Present' : 'Absent',
        record.updated_at ? new Date(record.updated_at).toLocaleString() : '',
        record.notes || ''
      ]);
      
      const csvContent = [headers, ...rows]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');
      
      const response = await apiService.sendAttendanceCSVEmail({
        sessionId: session.id,
        sessionTitle: session.title,
        courseName: session.course_name,
        courseCode: session.course_code,
        csvContent: csvContent,
        studentCount: attendanceRecords.length,
        presentCount: attendanceRecords.filter(r => r.status === 'present').length,
        absentCount: attendanceRecords.filter(r => r.status === 'absent').length
      });
      
      if (response.success) {
        setNotification({
          isOpen: true,
          type: 'success',
          title: 'Email Sent',
          message: 'Attendance report has been sent to your email successfully!'
        });
      } else {
        throw new Error(response.message || 'Failed to send attendance report email');
      }
    } catch (error) {
      console.error('Error sending attendance report email:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Email Failed',
        message: error.message || 'Failed to send attendance report email. Please try again.'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const records = attendanceRecords.map(record => ({
        studentId: record.student_id,
        status: record.status,
        notes: record.notes || null
      }));

      const response = await apiService.updateAttendanceRecords(sessionId, records);
      
      if (response.success) {
        setSuccess('Attendance records saved successfully');
        setHasUnsavedChanges(false);
        if (onAttendanceUpdated) {
          onAttendanceUpdated();
        }
      } else {
        setError(response.message || 'Failed to save attendance records');
      }
    } catch (error) {
      console.error('Error saving attendance records:', error);
      setError('Failed to save attendance records. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this attendance session? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      setError('');

      const response = await apiService.deleteAttendanceSession(sessionId);
      
      if (response.success) {
        if (onAttendanceUpdated) {
          onAttendanceUpdated();
        }
        onClose();
      } else {
        setError(response.message || 'Failed to delete attendance session');
      }
    } catch (error) {
      console.error('Error deleting attendance session:', error);
      setError('Failed to delete attendance session. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const formatCheckInTime = (timestamp) => {
    if (!timestamp) return null;
    
    try {
      const date = new Date(timestamp);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return null;
      }
      
      // Format as: 1/13/2026 12:23 AM
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      
      return `${month}/${day}/${year} ${displayHours}:${displayMinutes} ${ampm}`;
    } catch (error) {
      console.error('Error formatting check-in time:', error);
      return null;
    }
  };

  const generateQRCodeData = () => {
    if (!session) return null;
    
    // Create a unique attendance URL that students can scan
    const baseUrl = window.location.origin;
    const attendanceUrl = `${baseUrl}/portal/attendance/checkin/${sessionId}`;
    
    return attendanceUrl;
  };

  // Filter and sort students based on search term
  const filteredRecords = useMemo(() => {
    let filtered = attendanceRecords;
    
    // Apply status filter
    if (statusFilter !== 'all') {
      const beforeFilter = filtered.length;
      filtered = filtered.filter(record => {
        if (!record) return false;
        return record.status === statusFilter;
      });
      console.log(`🔍 Status filter "${statusFilter}": ${beforeFilter} → ${filtered.length} students`);
    }
    
    // Apply search filter
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase().trim();
      
      filtered = filtered
        .filter(record => {
          // Ensure we have valid data
          if (!record) return false;
          
          // Ensure we have a valid student_id (required for React keys)
          if (!record.student_id) return false;
          
          const fullName = (record.fullName || '').toLowerCase();
          const firstName = (record.first_name || '').toLowerCase();
          const lastName = (record.last_name || '').toLowerCase();
          
          // If no name data, skip this record
          if (!fullName && !firstName && !lastName) return false;
          
          // Check if search term matches any part of the name
          return fullName.includes(searchLower) || 
                 firstName.includes(searchLower) || 
                 lastName.includes(searchLower);
        })
        .sort((a, b) => {
          // Simple alphabetical sort when searching
          const nameA = (a.fullName || '').toLowerCase();
          const nameB = (b.fullName || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
    }

    // Debug logging for search results
    if (process.env.NODE_ENV === 'development' && searchTerm) {
      console.log('🔍 SEARCH RESULTS:', {
        searchTerm,
        totalRecords: attendanceRecords.length,
        filteredCount: filtered.length,
        filteredNames: filtered.map(r => r.fullName),
        filteredIds: filtered.map(r => r.id),
        allRecords: attendanceRecords.map(r => ({ id: r.id, name: r.fullName, first: r.first_name, last: r.last_name }))
      });
    }

    return filtered;
  }, [attendanceRecords, searchTerm, statusFilter]);

  // Calculate attendance summary
  const attendanceSummary = useMemo(() => {
    const presentStudents = attendanceRecords.filter(record => record.status === 'present');
    const absentStudents = attendanceRecords.filter(record => record.status === 'absent');
    
    return {
      present: presentStudents,
      absent: absentStudents,
      total: attendanceRecords.length,
      presentCount: presentStudents.length,
      absentCount: absentStudents.length,
      attendanceRate: attendanceRecords.length > 0 ? Math.round((presentStudents.length / attendanceRecords.length) * 100) : 0
    };
  }, [attendanceRecords]);

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <LoadingSpinner />
                <p>Loading attendance session...</p>
              </div>
            ) : (
              <>
                <ModalHeader>
              <ModalTitle>
                <FiUser size={24} style={{ color: 'var(--primary-color)' }} />
                Take Attendance
              </ModalTitle>
              <CloseButton onClick={onClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>


            {session && (
              <SessionInfo>
                <SessionTitle>
                  {session.title || `${session.course_name} - ${session.course_code}`}
                </SessionTitle>
                <SessionDetails>
                  <SessionDetail>
                    <FiCalendar size={16} />
                    {formatDateForDisplay(session.date, 'M/d/yyyy')}
                  </SessionDetail>
                  <SessionDetail>
                    <FiUser size={16} />
                    {session.instructor_first_name} {session.instructor_last_name}
                  </SessionDetail>
                  <SessionDetail>
                    <FiUsers size={16} />
                    {attendanceRecords.length} students enrolled
                  </SessionDetail>
                </SessionDetails>
                {attendanceRecords.length === 0 && (
                  <SessionNote>
                    <FiInfo size={14} />
                    No students are currently enrolled in this course
                  </SessionNote>
                )}
              </SessionInfo>
            )}

            {/* Dashboard-Style Attendance Stats */}
            <DashboardStatsCard>
              <StatsHeader>
                <StatsTitle>Attendance Overview</StatsTitle>
                <StatsToggleButton
                  onClick={() => setShowSummary(!showSummary)}
                  className={showSummary ? 'expanded' : ''}
                >
                  <FiChevronDown />
                  {showSummary ? 'Hide Details' : 'Show Details'}
                </StatsToggleButton>
              </StatsHeader>
              
              <StatsGrid>
                <StatItem>
                  <StatLabel>Total Students</StatLabel>
                  <StatValue>{attendanceSummary.total}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Checked In</StatLabel>
                  <StatValue className="present">{attendanceSummary.presentCount}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Absent</StatLabel>
                  <StatValue className="absent">{attendanceSummary.absentCount}</StatValue>
                </StatItem>
              </StatsGrid>
              
              {showSummary && (
                <DetailedAttendanceSummary style={{ marginTop: '1.5rem', marginBottom: 0 }}>
                  {/* Present Students */}
                  <SummarySection>
                    <SummaryTitle className="present">
                      <FiCheck />
                      Present Students ({attendanceSummary.presentCount})
                    </SummaryTitle>
                    {attendanceSummary.present.length > 0 ? (
                      <>
                        <SummaryStudentList>
                          {attendanceSummary.present.map((student) => (
                            <SummaryStudentName key={student.id || student.student_id} className="present">
                              <FiCheck />
                              {student.fullName}
                            </SummaryStudentName>
                          ))}
                        </SummaryStudentList>
                      </>
                    ) : (
                      <div style={{ 
                        padding: '1rem', 
                        textAlign: 'center', 
                        color: 'var(--text-muted)',
                        fontStyle: 'italic'
                      }}>
                        No students marked as present
                      </div>
                    )}
                  </SummarySection>
                  
                  {/* Absent Students */}
                  <SummarySection>
                    <SummaryTitle className="absent">
                      <FiX />
                      Absent Students ({attendanceSummary.absentCount})
                    </SummaryTitle>
                    {attendanceSummary.absent.length > 0 ? (
                      <>
                        <SummaryStudentList>
                          {attendanceSummary.absent.map((student) => (
                            <SummaryStudentName key={student.id || student.student_id} className="absent">
                              <FiX />
                              {student.fullName}
                            </SummaryStudentName>
                          ))}
                        </SummaryStudentList>
                      </>
                    ) : (
                      <div style={{ 
                        padding: '1rem', 
                        textAlign: 'center', 
                        color: 'var(--text-muted)',
                        fontStyle: 'italic'
                      }}>
                        No students marked as absent
                      </div>
                    )}
                  </SummarySection>
                  
                  {/* Summary Statistics */}
                  <SummaryStats>
                    <span>Total Students: {attendanceSummary.total}</span>
                    <span>Attendance Rate: {attendanceSummary.attendanceRate}%</span>
                  </SummaryStats>
                </DetailedAttendanceSummary>
              )}
            </DashboardStatsCard>

            {error && (
              <ErrorMessage
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                {error}
              </ErrorMessage>
            )}

            {success && (
              <SuccessMessage
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <FiCheck size={16} />
                {success}
              </SuccessMessage>
            )}

            {attendanceRecords.length === 0 ? (
              <EmptyState>
                <EmptyIcon>👥</EmptyIcon>
                <EmptyTitle>No Students Enrolled</EmptyTitle>
                <EmptyDescription>
                  There are no students enrolled in this course yet. 
                  Students need to be enrolled before you can take attendance.
                </EmptyDescription>
                <EmptyAction>
                  <Button 
                    type="button" 
                    className="secondary" 
                    onClick={() => window.open('/admin/courses', '_blank')}
                  >
                    <FiUsers size={16} />
                    Manage Course Enrollments
                  </Button>
                </EmptyAction>
              </EmptyState>
            ) : (
              <>
                <ExportButtonsContainer>
                  <ExportButton
                    onClick={downloadAttendanceCSV}
                    disabled={saving}
                  >
                    <FiDownload size={18} />
                    Download Attendance Report
                  </ExportButton>
                  <ExportButton
                    onClick={emailAttendanceCSV}
                    disabled={sendingEmail || saving}
                  >
                    {sendingEmail ? (
                      <LoadingSpinner />
                    ) : (
                      <FiMail size={18} />
                    )}
                    {sendingEmail ? 'Sending...' : 'Email Attendance Report'}
                  </ExportButton>
                </ExportButtonsContainer>
                
                <SearchContainer>
                  <SearchInputContainer>
                    <SearchIcon>
                      <FiSearch size={16} />
                    </SearchIcon>
                    <SearchInput
                      type="text"
                      placeholder="Search by first name or last name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <ClearSearchButton onClick={() => setSearchTerm('')} title="Clear search">
                        <FiX size={16} />
                      </ClearSearchButton>
                    )}
                    {searchTerm && (
                      <SearchResults>
                        {filteredRecords.length === 0 ? (
                          <>No students found matching "{searchTerm}"</>
                        ) : (
                          <>Showing {filteredRecords.length} of {attendanceRecords.length} students</>
                        )}
                      </SearchResults>
                    )}
                  </SearchInputContainer>
                  
                  <StatusFilter
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Students</option>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                  </StatusFilter>
                </SearchContainer>
                
                
                {filteredRecords.length === 0 ? (
                  <EmptyState>
                    <EmptyIcon>🔍</EmptyIcon>
                    <EmptyTitle>No Students Found</EmptyTitle>
                    <EmptyDescription>
                      No students match your search criteria. Try adjusting your search term.
                    </EmptyDescription>
                  </EmptyState>
                ) : (
                  <>
                    <BulkActionsContainer>
                      <SelectionInfo>
                        <SelectAllButton onClick={handleSelectAll}>
                          {filteredRecords.every(record => selectedStudents.has(record.student_id)) ? (
                            <FiCheckSquare size={16} />
                          ) : (
                            <FiSquare size={16} />
                          )}
                          {filteredRecords.every(record => selectedStudents.has(record.student_id)) 
                            ? 'Deselect All' 
                            : 'Select All'
                          }
                        </SelectAllButton>
                        <span>
                          {selectedStudents.size} of {filteredRecords.length} selected
                        </span>
                      </SelectionInfo>
                      {selectedStudents.size > 1 && (
                        <BulkActionButtons>
                          <BulkActionButton
                            className="present"
                            onClick={() => handleBulkStatusChange('present')}
                            disabled={selectedStudents.size === 0 || saving}
                          >
                            {saving ? (
                              <LoadingSpinner />
                            ) : (
                              <FiCheck size={18} />
                            )}
                            {saving ? 'Saving...' : 'Mark as Present'}
                          </BulkActionButton>
                          <BulkActionButton
                            className="absent"
                            onClick={() => handleBulkStatusChange('absent')}
                            disabled={selectedStudents.size === 0 || saving}
                          >
                            {saving ? (
                              <LoadingSpinner />
                            ) : (
                              <FiX size={18} />
                            )}
                            {saving ? 'Saving...' : 'Mark as Absent'}
                          </BulkActionButton>
                        </BulkActionButtons>
                      )}
                    </BulkActionsContainer>
                    
                    <StudentList>
                      {(() => {
                        // Debug logging for rendering
                        if (process.env.NODE_ENV === 'development' && searchTerm) {
                          console.log('🔍 RENDERING STUDENTS:', {
                            searchTerm,
                            filteredRecordsCount: filteredRecords.length,
                            filteredRecordsNames: filteredRecords.map(r => r.fullName),
                            filteredRecordsIds: filteredRecords.map(r => r.id)
                          });
                        }
                        return filteredRecords.map((record, index) => {
                          const isNotesExpanded = expandedNotes.has(record.student_id);
                          const timestamp = record.updated_at 
                            ? formatCheckInTime(record.updated_at)
                            : null;
                          const statusText = record.status === 'present' ? 'Present' : 'Absent';
                          const timestampLabel = record.status === 'present' 
                            ? (timestamp ? `Checked in • ${timestamp}` : 'Not checked in yet')
                            : (timestamp ? `Marked absent • ${timestamp}` : 'Not checked in yet');
                          
                          return (
                            <StudentItem 
                              key={record.id || `student-${record.student_id}-${index}`}
                            >
                              <StudentRow>
                                {/* Left: Checkbox + Avatar + Name + Email */}
                                <StudentRowLeft>
                                  <CheckboxContainer>
                                    <Checkbox
                                      className={`interactive ${selectedStudents.has(record.student_id) ? 'checked' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStudentSelection(record.student_id);
                                      }}
                                      title={selectedStudents.has(record.student_id) ? 'Deselect' : 'Select'}
                                    >
                                      {selectedStudents.has(record.student_id) ? (
                                        <FiCheckSquare size={16} />
                                      ) : (
                                        <FiSquare size={16} />
                                      )}
                                    </Checkbox>
                                  </CheckboxContainer>
                                  <StudentAvatar>
                                    {getInitials(record.first_name, record.last_name)}
                                  </StudentAvatar>
                                  <StudentInfo>
                                    <StudentName>{record.fullName}</StudentName>
                                    <StudentEmail>{record.email}</StudentEmail>
                                  </StudentInfo>
                                </StudentRowLeft>

                                {/* Middle: Status Pill + Timestamp */}
                                <StudentRowMiddle>
                                  <StatusPill className={record.status}>
                                    {statusText}
                                  </StatusPill>
                                  <StatusTimestamp>
                                    {timestampLabel}
                                  </StatusTimestamp>
                                </StudentRowMiddle>

                                {/* Right: Action Buttons */}
                                <StudentRowRight>
                                  <StatusActionButton
                                    className="interactive"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(record.student_id, record.status === 'present' ? 'absent' : 'present');
                                    }}
                                  >
                                    {record.status === 'present' ? 'Mark Absent' : 'Mark Present'}
                                  </StatusActionButton>
                                  <NotesToggleButton
                                    className="interactive"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleNotesExpansion(record.student_id);
                                    }}
                                  >
                                    {isNotesExpanded ? 'Hide note' : 'Add note'}
                                  </NotesToggleButton>
                                </StudentRowRight>
                              </StudentRow>

                              {/* Collapsible Notes */}
                              {isNotesExpanded && (
                                <NotesContainer>
                                  <NotesInput
                                    className="interactive"
                                    placeholder="Add a note…"
                                    value={record.notes || ''}
                                    onChange={(e) => handleNotesChange(record.student_id, e.target.value)}
                                    maxLength={500}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </NotesContainer>
                              )}
                            </StudentItem>
                          );
                        });
                      })()}
                    </StudentList>
                  </>
                )}
              </>
            )}


            {showQRCode && generateQRCodeData() && (
              <div style={{ margin: '1.5rem 0', display: 'flex', justifyContent: 'center' }}>
                <QRCodeGenerator
                  data={generateQRCodeData()}
                  title={`${session?.title || 'Attendance'} - QR Code`}
                  description={
                    session?.checkin_start_time && session?.checkin_end_time
                      ? `Students can scan this QR code to check in for attendance. Check-in window: ${session.checkin_start_time} - ${session.checkin_end_time}`
                      : "Students can scan this QR code to check in for attendance"
                  }
                  size={200}
                  showData={false}
                  showActions={true}
                />
              </div>
            )}

            <ButtonGroup>
              <Button 
                type="button" 
                className="danger" 
                onClick={handleDelete}
                disabled={saving}
              >
                <FiTrash2 size={16} />
                Delete Session
              </Button>
              <Button 
                type="button" 
                className="secondary" 
                onClick={() => setShowQRCode(!showQRCode)}
                disabled={saving}
              >
                <FiGrid size={16} />
                {showQRCode ? 'Hide QR Code' : 'Show QR Code'}
              </Button>
              <Button type="button" className="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="button" 
                className="primary" 
                onClick={handleSave}
                disabled={saving || !hasUnsavedChanges}
              >
                {saving ? (
                  <>
                    <LoadingSpinner />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave size={16} />
                    {hasUnsavedChanges ? 'Save Changes' : 'No Changes'}
                  </>
                )}
              </Button>
            </ButtonGroup>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </AnimatePresence>
  );
}

export default TakeAttendanceModal;
